import { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import PrefetchLink from '@/components/PrefetchLink';
import { Menu, X, ChevronDown, Download, FileText } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import logoColor from '@/assets/logo-mtb-btp-color.webp';
import { getProfileUrl, getProfileDownloadName } from '@/constants/companyProfile';

const Header = () => {
  const { language, setLanguage, t } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isFr = language === 'fr';

  const navGroups = [
    { path: '/', label: t('nav.home') },
    {
      label: isFr ? 'Entreprise' : 'Company',
      children: [
        { path: '/about', label: t('nav.about') },
        { path: '/methodology', label: t('nav.methodology') },
        { path: '/compliance', label: t('nav.compliance') },
        { path: '/partners', label: isFr ? 'Partenaires & Clients' : 'Partners & Clients' },
        { path: '/careers', label: isFr ? 'Carrières' : 'Careers' },
        { path: '/news', label: t('nav.news') },
        { path: '/documents', label: isFr ? 'Documents & modèles' : 'Documents & templates' },
        { path: '/faq', label: t('nav.faq') },
      ],
    },
    { path: '/services', label: t('nav.services') },
    {
      label: isFr ? 'Réalisations' : 'Projects',
      children: [
        { path: '/references', label: t('nav.references') },
        { path: '/gallery', label: t('nav.gallery') },
      ],
    },
    { path: '/tenders', label: t('nav.tenders') },
    { path: '/quote-request', label: isFr ? 'Demande de devis' : 'Request a quote' },
  ];

  // Mobile: mêmes rubriques principales + rubriques secondaires regroupées
  const mobileNavItems = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: isFr ? 'Entreprise' : 'Company' },
    { path: '/services', label: t('nav.services') },
    { path: '/references', label: isFr ? 'Réalisations' : 'Projects' },
    { path: '/tenders', label: t('nav.tenders') },
    { path: '/quote-request', label: isFr ? 'Demande de devis' : 'Request a quote' },
    { path: '/contact', label: t('nav.contact') },
  ];

  const mobileSecondaryItems = [
    { path: '/methodology', label: t('nav.methodology') },
    { path: '/compliance', label: t('nav.compliance') },
    { path: '/gallery', label: t('nav.gallery') },
    { path: '/partners', label: isFr ? 'Partenaires' : 'Partners' },
    { path: '/careers', label: isFr ? 'Carrières' : 'Careers' },
    { path: '/news', label: t('nav.news') },
    { path: '/documents', label: isFr ? 'Documents' : 'Documents' },
    { path: '/faq', label: t('nav.faq') },
    { path: '/appointment', label: isFr ? 'Prendre rendez-vous' : 'Book appointment' },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 bg-primary shadow-lg">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <PrefetchLink to="/" className="flex items-center">
            <img 
              src={logoColor} 
              alt="MTB BTP" 
              className="h-14 md:h-16 w-auto"
              width={256}
              height={171}
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
          </PrefetchLink>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1" ref={dropdownRef}>
            {navGroups.map((item) =>
              'children' in item && item.children ? (
                <div key={item.label} className="relative">
                  <button
                    onClick={() => setOpenDropdown(openDropdown === item.label ? null : item.label)}
                    className={`flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors rounded-md ${
                      item.children.some((c) => isActive(c.path))
                        ? 'text-accent bg-primary-foreground/10'
                        : 'text-primary-foreground hover:text-accent hover:bg-primary-foreground/5'
                    }`}
                  >
                    {item.label}
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openDropdown === item.label ? 'rotate-180' : ''}`} />
                  </button>
                  {openDropdown === item.label && (
                    <div className="absolute top-full left-0 mt-1 w-56 bg-card border border-border rounded-lg shadow-xl py-1 z-50 animate-fade-in">
                      {item.children.map((child) => (
                        <PrefetchLink
                          key={child.path}
                          to={child.path}
                          onClick={() => setOpenDropdown(null)}
                          className={`block px-4 py-2.5 text-sm transition-colors ${
                            isActive(child.path)
                              ? 'text-accent bg-accent/10 font-medium'
                              : 'text-foreground hover:text-accent hover:bg-accent/5'
                          }`}
                        >
                          {child.label}
                        </PrefetchLink>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <PrefetchLink
                  key={item.path}
                  to={item.path!}
                  className={`px-3 py-2 text-sm font-medium transition-colors rounded-md ${
                    isActive(item.path!)
                      ? 'text-accent bg-primary-foreground/10'
                      : 'text-primary-foreground hover:text-accent hover:bg-primary-foreground/5'
                  }`}
                >
                  {item.label}
                </PrefetchLink>
              )
            )}
          </nav>

          {/* Language Switcher & Contact CTA */}
          <div className="hidden lg:flex items-center space-x-4">
            <div className="flex items-center bg-primary-foreground/10 rounded-md p-1">
              <button
                onClick={() => setLanguage('fr')}
                className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                  language === 'fr'
                    ? 'bg-accent text-accent-foreground'
                    : 'text-primary-foreground hover:text-accent'
                }`}
              >
                FR
              </button>
              <button
                onClick={() => setLanguage('en')}
                className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                  language === 'en'
                    ? 'bg-accent text-accent-foreground'
                    : 'text-primary-foreground hover:text-accent'
                }`}
              >
                EN
              </button>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="hidden xl:inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-primary-foreground hover:text-accent rounded-md transition-colors"
                  aria-label={`PDF – ${t('cta.profile.button')}`}
                >
                  <Download className="w-4 h-4" />
                  <span>PDF</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-popover">
                <DropdownMenuItem asChild>
                  <a
                    href={getProfileUrl('fr')}
                    download={getProfileDownloadName('fr')}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <FileText className="w-4 h-4" />
                    {t('cta.profile.fr')}
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a
                    href={getProfileUrl('en')}
                    download={getProfileDownloadName('en')}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <FileText className="w-4 h-4" />
                    {t('cta.profile.en')}
                  </a>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <PrefetchLink to="/contact">
              <Button className="bg-accent text-accent-foreground hover:bg-mtb-gold-dark font-semibold">
                {t('nav.contact')}
              </Button>
            </PrefetchLink>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 text-primary-foreground hover:text-accent transition-colors"
            aria-label={isMenuOpen ? (language === 'fr' ? 'Fermer le menu' : 'Close menu') : (language === 'fr' ? 'Ouvrir le menu' : 'Open menu')}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden pb-4 animate-slide-down-menu overflow-hidden">
            <nav className="flex flex-col space-y-1">
              {mobileNavItems.map((item) => (
                <PrefetchLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`px-4 py-3 text-sm font-medium transition-colors duration-150 rounded-md tap-feedback ${
                    isActive(item.path)
                      ? 'text-accent bg-primary-foreground/10'
                      : 'text-primary-foreground hover:text-accent hover:bg-primary-foreground/5'
                  }`}
                >
                  {item.label}
                </PrefetchLink>
              ))}
            </nav>
            <div className="mt-4 pt-3 border-t border-primary-foreground/15">
              <p className="px-4 pb-2 text-[11px] font-semibold uppercase tracking-wider text-primary-foreground/50">
                {isFr ? 'Plus d’informations' : 'More information'}
              </p>
              <nav className="grid grid-cols-2 gap-1">
                {mobileSecondaryItems.map((item) => (
                  <PrefetchLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={`px-4 py-2.5 text-sm rounded-md tap-feedback transition-colors ${
                      isActive(item.path)
                        ? 'text-accent bg-primary-foreground/10'
                        : 'text-primary-foreground/80 hover:text-accent hover:bg-primary-foreground/5'
                    }`}
                  >
                    {item.label}
                  </PrefetchLink>
                ))}
              </nav>
            </div>
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-primary-foreground/20">
              <div className="flex items-center bg-primary-foreground/10 rounded-md p-1">
                <button
                  onClick={() => setLanguage('fr')}
                  className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                    language === 'fr'
                      ? 'bg-accent text-accent-foreground'
                      : 'text-primary-foreground hover:text-accent'
                  }`}
                >
                  FR
                </button>
                <button
                  onClick={() => setLanguage('en')}
                  className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                    language === 'en'
                      ? 'bg-accent text-accent-foreground'
                      : 'text-primary-foreground hover:text-accent'
                  }`}
                >
                  EN
                </button>
              </div>
              <PrefetchLink to="/contact" onClick={() => setIsMenuOpen(false)}>
                <Button className="bg-accent text-accent-foreground hover:bg-mtb-gold-dark font-semibold">
                  {t('nav.contact')}
                </Button>
              </PrefetchLink>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
